import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useSearchPromosByToggle } from '~/hooks/apis/promo/useSearchPromosByToggle';
import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';
import useDebounced from '~/hooks/useDebounced';

import type { PromoMeta, SearchPromosByToggleQuery, SearchPromosByToggleResponse } from '~/services/PromoService';

export type PromoByToggleSearchResults = SearchPromosByToggleResponse['result'];
export type PromoByToggleEntity = SearchPromosByToggleQuery['entity'];
export type PromoByToggleDisplayMode = 'standard' | 'refined';
type Toggle = SearchPromosByToggleQuery['toggle'];
type ToggleSearchScope = 'all' | 'configured-toggle' | 'no-toggles-set' | 'ignore-toggles';
type ReservedQuerySet = 'NO_TOGGLES_SET' | 'IGNORE_TOGGLES';
export type ToggleSearchRelatedFeature = Extract<SearchPromosByToggleQuery['is-related-to'], string>;

export const toggleSearchRelatedFeatures = {
  promo: [
    { value: 'promo-display-config', label: 'Promo Display' },
    { value: 'marketing-router', label: 'Marketing Router' },
  ],
  voucher: [
    { value: 'voucher-display-config', label: 'Voucher Display V1' },
    { value: 'marketing-router', label: 'Marketing Router' },
    { value: 'voucher-auto-assignment-rules', label: 'Voucher auto-assignment' },
  ],
} as const satisfies Record<PromoByToggleEntity, ReadonlyArray<{ value: ToggleSearchRelatedFeature; label: string }>>;

/** The complete, presentation-independent value produced by a toggle lookup. */
export type PromoByToggleSearch = {
  entity: PromoByToggleEntity | 'all';
  results: Array<PromoByToggleSearchResult>;
  displayMode: PromoByToggleDisplayMode;
};

export type PromoByToggleSearchResult = PromoByToggleSearchResults[number] & {
  entity: PromoByToggleEntity;
};

type LockedNameProps =
  | { codeName?: never; voucherName?: never }
  | {
      /** Locks the lookup to this promo code name and hides the editable name and entity controls. */
      codeName: string;
      voucherName?: never;
    }
  | {
      /** Locks the lookup to this voucher name and hides the editable name and entity controls. */
      codeName?: never;
      voucherName: string;
    };

type Props = {
  promoMeta?: PromoMeta | null;
  /** Prevents a lookup until the caller makes this control active. */
  isActive?: boolean;
  /** Uses a reserved server query such as `IGNORE_TOGGLES` instead of a name lookup. */
  querySet?: ReservedQuerySet;
  /** Forces the related-feature request parameter and hides the optional checkbox. */
  includeRelatedFeaturesSet?: boolean;
  /** Fixes the results presentation and hides the display-mode selector. */
  displayModeSet?: PromoByToggleDisplayMode;
  /** Fixes the entity and hides the entity/toggle selectors for lookup-only use. */
  modeSet?: PromoByToggleEntity;
  /** Fixes the toggle and hides the entity/toggle selectors for lookup-only use. */
  toggleSet?: Toggle;
  /** Receives each completed lookup, or undefined while the lookup is changed or disabled. */
  onSearchChange?: (search?: PromoByToggleSearch) => void;
} & LockedNameProps;

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to search by toggle.';
}

const NAME_QUERY_DEBOUNCE_MS = 300;
const RESERVED_SCOPE_QUERIES = {
  'no-toggles-set': 'NO_TOGGLES_SET',
  'ignore-toggles': 'IGNORE_TOGGLES',
} as const;
const RESERVED_QUERY_SET_SCOPES: Record<ReservedQuerySet, ToggleSearchScope> = {
  NO_TOGGLES_SET: 'no-toggles-set',
  IGNORE_TOGGLES: 'ignore-toggles',
};

export default function PromoByToggle({
  promoMeta,
  codeName,
  voucherName,
  isActive = true,
  querySet,
  includeRelatedFeaturesSet,
  displayModeSet,
  modeSet,
  toggleSet,
  onSearchChange,
}: Props) {
  const { promoBrand: brand } = useCurrentPromoBrand();
  const [entity, setEntity] = useState<PromoByToggleEntity>('promo');
  const [toggle, setToggle] = useState<Toggle>();
  const [scope, setScope] = useState<ToggleSearchScope>('all');
  const [query, setQuery] = useState('');
  const [hasFocusedNameQuery, setHasFocusedNameQuery] = useState(false);
  const [includeExtendedResults, setIncludeExtendedResults] = useState(false);
  const [includeRelatedFeatures, setIncludeRelatedFeatures] = useState(false);
  const [displayMode, setDisplayMode] = useState<PromoByToggleDisplayMode>('standard');
  const debouncedQuery = useDebounced(query.trim(), NAME_QUERY_DEBOUNCE_MS);
  const selectedScope = querySet ? RESERVED_QUERY_SET_SCOPES[querySet] : scope;
  const lockedName = codeName ?? voucherName;
  const isNameLocked = lockedName !== undefined;
  const lockedEntity = codeName !== undefined ? 'promo' : voucherName !== undefined ? 'voucher' : undefined;
  const selectedEntity = lockedEntity ?? modeSet ?? entity;
  const isLookupOnly = isNameLocked || modeSet !== undefined || toggleSet !== undefined;
  const showToggleSelector = querySet === undefined && (!isLookupOnly || (isNameLocked && toggleSet === undefined));
  const selectedIncludeRelatedFeatures = includeRelatedFeaturesSet ?? includeRelatedFeatures;
  const selectedDisplayMode = displayModeSet ?? displayMode;
  const toggleOptions = selectedEntity === 'promo' ? promoMeta?.promo_toggles : promoMeta?.voucher_toggles;
  // Do not send a toggle that belongs to the other entity: the API rejects invalid
  // entity/toggle combinations, and the user must explicitly choose the first toggle.
  const selectedToggle =
    toggleSet ?? (toggleOptions?.some((option: { key: Toggle }) => option.key === toggle) ? toggle : undefined);
  // Reserved query sets are evaluated by the server and also need to work for
  // entities with no configured toggles. The contract still requires a toggle
  // string, so use an empty value for those queries rather than dropping them.
  const requestToggle: Toggle = selectedToggle ?? toggleOptions?.[0]?.key ?? ('' as Toggle);
  const lookupQuery = lockedName ?? (debouncedQuery || (hasFocusedNameQuery ? 'ALL_RESULTS' : ''));
  const scopeQuery =
    selectedScope === 'configured-toggle'
      ? lookupQuery
      : selectedScope === 'all'
        ? (lockedName ?? debouncedQuery) || 'ALL_RESULTS'
        : RESERVED_SCOPE_QUERIES[selectedScope];
  const request = useMemo<SearchPromosByToggleQuery | null>(() => {
    if (
      !isActive ||
      selectedScope === 'all' ||
      !scopeQuery ||
      (selectedScope === 'configured-toggle' && !selectedToggle)
    ) {
      return null;
    }

    const relatedFeatures = selectedIncludeRelatedFeatures
      ? toggleSearchRelatedFeatures[selectedEntity].map(
          (feature: { value: ToggleSearchRelatedFeature }) => feature.value,
        )
      : undefined;

    if (relatedFeatures) {
      return {
        entity: selectedEntity,
        toggle: requestToggle,
        query: scopeQuery,
        brand,
        ...(includeExtendedResults ? { limit: 50 } : {}),
        'is-related-to': relatedFeatures,
      };
    }

    return {
      entity: selectedEntity,
      toggle: requestToggle,
      query: scopeQuery,
      brand,
      ...(includeExtendedResults ? { limit: 50 } : {}),
    };
  }, [
    brand,
    includeExtendedResults,
    isActive,
    selectedScope,
    scopeQuery,
    selectedEntity,
    selectedIncludeRelatedFeatures,
    selectedToggle,
    requestToggle,
  ]);
  const allPromoRequest = useMemo<SearchPromosByToggleQuery | null>(() => {
    const toggle = promoMeta?.promo_toggles[0]?.key;
    if (!isActive || selectedScope !== 'all' || !toggle || !scopeQuery) {
      return null;
    }

    return {
      entity: 'promo',
      toggle,
      query: scopeQuery,
      brand,
      ...(includeExtendedResults ? { limit: 50 } : {}),
      ...(selectedIncludeRelatedFeatures
        ? { 'is-related-to': toggleSearchRelatedFeatures.promo.map((feature) => feature.value) }
        : {}),
    };
  }, [
    brand,
    includeExtendedResults,
    isActive,
    promoMeta?.promo_toggles,
    selectedScope,
    scopeQuery,
    selectedIncludeRelatedFeatures,
  ]);
  const allVoucherRequest = useMemo<SearchPromosByToggleQuery | null>(() => {
    const toggle = promoMeta?.voucher_toggles[0]?.key;
    if (!isActive || selectedScope !== 'all' || !toggle || !scopeQuery) {
      return null;
    }

    return {
      entity: 'voucher',
      toggle,
      query: scopeQuery,
      brand,
      ...(includeExtendedResults ? { limit: 50 } : {}),
      ...(selectedIncludeRelatedFeatures
        ? { 'is-related-to': toggleSearchRelatedFeatures.voucher.map((feature) => feature.value) }
        : {}),
    };
  }, [
    brand,
    includeExtendedResults,
    isActive,
    promoMeta?.voucher_toggles,
    selectedScope,
    scopeQuery,
    selectedIncludeRelatedFeatures,
  ]);
  const { data, error, isFetching } = useSearchPromosByToggle(request);
  const {
    data: allPromoData,
    error: allPromoError,
    isFetching: isFetchingAllPromos,
  } = useSearchPromosByToggle(allPromoRequest);
  const {
    data: allVoucherData,
    error: allVoucherError,
    isFetching: isFetchingAllVouchers,
  } = useSearchPromosByToggle(allVoucherRequest);

  useEffect(() => {
    onSearchChange?.();
  }, [onSearchChange, request]);

  useEffect(() => {
    if (selectedScope === 'all') {
      if (!allPromoData && !allVoucherData) {
        return;
      }
      onSearchChange?.({
        entity: 'all',
        results: [
          ...(allPromoData?.result.map((result) => ({ ...result, entity: 'promo' as const })) ?? []),
          ...(allVoucherData?.result.map((result) => ({ ...result, entity: 'voucher' as const })) ?? []),
        ],
        displayMode: selectedDisplayMode,
      });
    } else if (data) {
      onSearchChange?.({
        entity: selectedEntity,
        results: data.result.map((result) => ({ ...result, entity: selectedEntity })),
        displayMode: selectedDisplayMode,
      });
    }
  }, [allPromoData, allVoucherData, data, onSearchChange, selectedDisplayMode, selectedEntity, selectedScope]);

  function handleEntityChange(nextEntity: PromoByToggleEntity) {
    setEntity(nextEntity);
  }

  function handleToggleChange(nextToggle: string) {
    const matchingToggle = toggleOptions?.find((option) => option.key === nextToggle);
    if (matchingToggle) {
      setToggle(matchingToggle.key);
      setScope('configured-toggle');
    }
  }

  function handleScopeChange(nextScope: ToggleSearchScope) {
    setScope(nextScope);
    if (nextScope === 'no-toggles-set' || nextScope === 'ignore-toggles') {
      setHasFocusedNameQuery(true);
    }
  }

  return (
    <Box mt={4}>
      <Stack spacing={2}>
        {!isLookupOnly && (
          <Box>
            <Typography variant="h6">Promo and voucher toggle search</Typography>
            <Typography variant="body2" color="text.secondary">
              Search all promos and vouchers, or narrow results by toggle.
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          {!isLookupOnly && selectedScope !== 'all' && (
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                select
                label="Entity"
                value={selectedEntity}
                onChange={(event) => {
                  if (event.target.value === 'promo' || event.target.value === 'voucher') {
                    handleEntityChange(event.target.value);
                  }
                }}
                fullWidth
              >
                <MenuItem value="promo">Promo</MenuItem>
                <MenuItem value="voucher">Voucher</MenuItem>
              </TextField>
            </Grid>
          )}
          {showToggleSelector && (
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Toggle or scope"
                value={scope === 'configured-toggle' ? (selectedToggle ?? '') : scope}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === 'all' || value === 'no-toggles-set' || value === 'ignore-toggles') {
                    handleScopeChange(value);
                  } else {
                    handleToggleChange(value);
                  }
                }}
                slotProps={{ select: { displayEmpty: true } }}
                fullWidth
              >
                <MenuItem value="" disabled>
                  Select a toggle or scope
                </MenuItem>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="no-toggles-set">No toggles set</MenuItem>
                <MenuItem value="ignore-toggles">Ignore toggles</MenuItem>
                {toggleOptions?.map((option: { key: Toggle }) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.key}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {!isNameLocked && (
            <Grid size={{ xs: 12, md: isLookupOnly ? 8 : 4 }}>
              <TextField
                label="Name query"
                value={query}
                onFocus={() => setHasFocusedNameQuery(true)}
                onChange={(event) => setQuery(event.target.value)}
                fullWidth
              />
            </Grid>
          )}
          {!displayModeSet && (
            <Grid size={{ xs: 12, md: isLookupOnly ? 4 : 3 }}>
              <TextField
                select
                label="Display mode"
                value={selectedDisplayMode}
                onChange={(event) => {
                  if (event.target.value === 'standard' || event.target.value === 'refined') {
                    setDisplayMode(event.target.value);
                  }
                }}
                fullWidth
              >
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="refined">Refined</MenuItem>
              </TextField>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeExtendedResults}
                    onChange={(event) => setIncludeExtendedResults(event.target.checked)}
                  />
                }
                label="Extended results (up to 50)"
              />
              {includeRelatedFeaturesSet === undefined && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedIncludeRelatedFeatures}
                      onChange={(event) => setIncludeRelatedFeatures(event.target.checked)}
                    />
                  }
                  label="Include related features"
                />
              )}
            </Stack>
          </Grid>
        </Grid>
        {(isFetching || isFetchingAllPromos || isFetchingAllVouchers) && (
          <CircularProgress size={24} aria-label="Searching promos and vouchers" />
        )}
        {(error || allPromoError || allVoucherError) && (
          <Alert severity="error">{getErrorMessage(error ?? allPromoError ?? allVoucherError)}</Alert>
        )}
      </Stack>
    </Box>
  );
}
